import { cosmosHelper } from 'lib/cosmos/cosmosHelper';
import { DateTime, DateTimeFormatOptions, StringUnitLength, Zone } from 'luxon';

type TYear = `${number}${number}${number}${number}`;
type TMonth = `${number}${number}`;
type TDay = `${number}${number}`;
type THours = `${number}${number}`;
type TMinutes = `${number}${number}`;
type TSeconds = `${number}${number}`;
type TMilliseconds = `${number}${number}${number}`;

export type DateSource = Date | string | DateTime;

/**
 * Represent a string like `2021-01-08`
 */
export type ISODate = `${TYear}-${TMonth}-${TDay}`;

/**
 * Represent a string like `14:42:34.678`
 */
export type ISOTime = `${THours}:${TMinutes}:${TSeconds}.${TMilliseconds}`;

/**
 * Represent a string like `2021-01-08T14:42:34.678Z` (format: ISO 8601).
 *
 * It is not possible to type more precisely (list every possible values for months, hours etc) as
 * it would result in a warning from TypeScript:
 *   "Expression produces a union type that is too complex to represent. ts(2590)
 */
export type ISODateTime = `${ISODate}T${ISOTime}Z`;

export class CoreTimeService {
	/**
	 * Cached zone because intl API is slow. More info in https://github.com/moment/luxon/issues/431
	 */
	public readonly currentZone: Zone;

	constructor() {
		// resolve zone once because it's an expensive operation
		this.currentZone = DateTime.local().zone;
	}

	now(): DateTime {
		return this.getLuxonDateTime(new Date());
	}

	nowCosmosDb(): string {
		return cosmosHelper.formatCosmosDbDate(new Date());
	}

	toISODate(date: DateTime): ISODate {
		const formattedDate = this.toCleanUtcDate(date).toISODate();

		if (!formattedDate) {
			throw Error(`Invalid date format ${date.toJSON()}`);
		}

		return formattedDate as ISODate;
	}

	toCosmosDbDate(dateTime: DateTime | Date): string {
		if (dateTime instanceof Date) {
			return this.getLuxonDateTime(dateTime).toUTC().toJSDate().toJSON();
		}
		return dateTime.toUTC().toJSDate().toJSON();
	}

	getLuxonDateTime(source: DateSource): DateTime {
		let dateTime: DateTime;

		if (source instanceof Date) {
			dateTime = DateTime.fromJSDate(source);
		} else if (source instanceof DateTime) {
			dateTime = source;
		} else {
			dateTime = DateTime.fromISO(source);
		}

		return dateTime.setZone(this.currentZone);
	}

	fromNow(source: DateSource, style: StringUnitLength = 'long'): string {
		if (this.getDifferenceInSeconds(this.now(), source) <= 60) {
			return `Just now`;
		}

		if (style === 'narrow') {
			return this.customFromNowNarrow(source);
		}

		return (
			this.getLuxonDateTime(source).toRelative({
				style: style
			}) ?? ''
		);
	}

	fromNowCalendar(source: DateSource): string {
		return this.getLuxonDateTime(source).toRelativeCalendar({}) ?? '';
	}

	toDate(source: DateSource): string {
		return this.getLuxonDateTime(source).toLocaleString();
	}

	getAge(birtdate: DateSource): number {
		return Math.floor(this.getDifferenceInYears(this.now(), birtdate));
	}

	getDifferenceInYears(date1: DateSource, date2: DateSource): number {
		const a = this.getLuxonDateTime(date1);
		const b = this.getLuxonDateTime(date2);

		if (a > b) {
			return a.diff(b, 'years').years;
		}
		return b.diff(a, 'years').years;
	}

	getDifferenceInMonths(date1: DateSource, date2: DateSource): number {
		const a = this.getLuxonDateTime(date1);
		const b = this.getLuxonDateTime(date2);

		if (a > b) {
			return a.diff(b, 'months').months;
		}
		return b.diff(a, 'months').months;
	}

	getDifferenceInDays(date1: DateSource, date2: DateSource): number {
		const a = this.getLuxonDateTime(date1);
		const b = this.getLuxonDateTime(date2);

		if (a > b) {
			return a.diff(b, 'days').days;
		}
		return b.diff(a, 'days').days;
	}

	getDifferenceInSeconds(date1: DateSource, date2: DateSource): number {
		const a = this.getLuxonDateTime(date1);
		const b = this.getLuxonDateTime(date2);

		if (a > b) {
			return a.diff(b, 'seconds').seconds;
		}
		return b.diff(a, 'seconds').seconds;
	}

	getDifferenceInMinutes(date1: DateSource, date2: DateSource): number {
		const a = this.getLuxonDateTime(date1);
		const b = this.getLuxonDateTime(date2);

		if (a > b) {
			return a.diff(b, 'minutes').minutes;
		}
		return b.diff(a, 'minutes').minutes;
	}

	getDifferenceInHours(date1: DateSource, date2: DateSource): number {
		const a = this.getLuxonDateTime(date1);
		const b = this.getLuxonDateTime(date2);

		if (a > b) {
			return a.diff(b, 'hours').hours;
		}
		return b.diff(a, 'hours').hours;
	}

	formatDate(date: DateSource): string {
		return this.formatLocaleString(date, DateTime.DATE_MED);
	}

	formatDateTime(date: DateSource): string {
		return this.formatLocaleString(date, DateTime.DATETIME_MED);
	}

	formatDateWithoutYear(date: DateSource): string {
		return this.formatCustom(date, 'MMM d');
	}

	formatDayAndMonth(date: DateSource): string {
		return this.formatCustom(this.getLuxonDateTime(date), 'MMM d');
	}

	formatDayMonthAndYear(date: DateSource): string {
		return this.formatCustom(this.getLuxonDateTime(date), 'MMM d yyyy');
	}

	toCleanUtcDate(date: DateTime): DateTime {
		return this.now().toUTC().set({
			day: date.day,
			month: date.month,
			year: date.year,
			minute: 0,
			millisecond: 0,
			hour: 0,
			second: 0
		});
	}

	/**
	 * https://moment.github.io/luxon/docs/manual/formatting
	 */
	private formatCustom(date: DateSource, format: string): string {
		return this.getLuxonDateTime(date).toFormat(format);
	}

	/**
	 * https://moment.github.io/luxon/docs/manual/formatting
	 */
	private formatLocaleString(date: DateSource, options?: DateTimeFormatOptions): string {
		return this.getLuxonDateTime(date).toLocaleString(options);
	}

	private customFromNowNarrow(source: DateSource): string {
		const luxonDateTime = this.getLuxonDateTime(source);
		const now = this.now();

		const differenceInSeconds = Math.floor(this.getDifferenceInSeconds(luxonDateTime, now));

		if (differenceInSeconds <= 60) {
			return `${differenceInSeconds}s`;
		}

		const differenceInMinutes = Math.floor(this.getDifferenceInMinutes(luxonDateTime, now));

		if (differenceInMinutes <= 60) {
			return `${differenceInMinutes}m`;
		}

		const differenceInHours = Math.floor(this.getDifferenceInHours(luxonDateTime, now));

		if (differenceInHours <= 24) {
			return `${differenceInHours}h`;
		}

		const differenceInDays = Math.floor(this.getDifferenceInDays(luxonDateTime, now));

		if (differenceInDays <= 30) {
			return `${differenceInDays}d`;
		}

		const differenceInMonths = Math.floor(this.getDifferenceInMonths(luxonDateTime, now));

		if (differenceInMonths <= 12) {
			return `${differenceInMonths}mo`;
		}

		const differenceInYears = Math.floor(this.getDifferenceInYears(luxonDateTime, now));

		return `${differenceInYears}y`;
	}
}
